import React from 'react';
import { View, Font, Svg, Circle, Polyline, G, Path, Text, Line } from '@react-pdf/renderer';
import { useGauge } from "use-gauge";

Font.register({
  family: 'Inter',
  fonts: [
    { src: `${process.env.PUBLIC_URL}/font/Inter-Regular.ttf` },
    { src: `${process.env.PUBLIC_URL}/font/Inter-Medium.ttf`, fontWeight: 500 },
    { src: `${process.env.PUBLIC_URL}/font/Inter-SemiBold.ttf`, fontWeight: 600 },
    { src: `${process.env.PUBLIC_URL}/font/Inter-Bold.ttf`, fontWeight: 700 }
  ]
});

Font.registerHyphenationCallback(word => [word]);

interface ArcedProps {
  value: number;
  color: string;
  prevColor: string; 
  prevValue?: number;
}

const START_ANGLE = 45;
const END_ANGLE = 315;

const constrainValue = (value: number, min: number, max: number) => {
  if (value < min) return min;
  if (value > max) return max;
  return value;
};

export function Arced(props: ArcedProps) {
  const { value, color, prevColor, prevValue } = props;
  const constrainedValue = constrainValue(value, 0, 100);
  const constrainedPrevValue = prevValue ? constrainValue(prevValue, 0, 100) : null;

  const gauge = useGauge({
    domain: [0, 100],
    startAngle: START_ANGLE,
    endAngle: END_ANGLE,
    numTicks: 21,
    diameter: 300
  });

  const needle = gauge.getNeedleProps({
    value: constrainedValue,
    baseRadius: 14,
    tipRadius: 1
  });

  const prevNeedle = constrainedPrevValue !== null && constrainedPrevValue !== 0
    ? gauge.getNeedleProps({
      value: constrainedPrevValue,
      baseRadius: 14,
      tipRadius: 1,
    })
    : null;

  return (
    <View style={{ padding: 0 }}>
      <Svg
        style={{
          width: '100%',
          overflow: 'visible',
          padding: 0,
        }}
        {...gauge.getSVGProps()}
      >
        <G id="arcs" style={{ transform: 'scale(0.7, 0.7)' }}>
          <Path
            {...gauge.getArcProps({
              offset: 30,
              startAngle: START_ANGLE,
              endAngle: END_ANGLE
            })}
            fill="none"
            style={{ stroke: '#639754' }}
            strokeLinecap="round"
            strokeWidth={30}
          />
          <Path
            {...gauge.getArcProps({
              offset: 30,
              startAngle: START_ANGLE,
              endAngle: 140
            })}
            fill="none"
            style={{ stroke: '#e03c32' }}
            strokeLinecap="round"
            strokeWidth={30}
          />
          <Path
            {...gauge.getArcProps({
              offset: 30,
              startAngle: 140,
              endAngle: 180
            })}
            fill="none"
            style={{ stroke: '#ffd301' }}
            strokeWidth={30}
          />
          <Path
            {...gauge.getArcProps({
              offset: 30,
              startAngle: 180,
              endAngle: 220
            })}
            fill="none"
            style={{ stroke: '#7bb662' }}
            strokeWidth={30}
          />
        </G>
        <G id="ticks" style={{ transform: 'scale(0.7, 0.7)' }}>
          {gauge.ticks.map((angle) => {
            const asValue = gauge.angleToValue(angle);
            const showText = asValue === 35 || asValue === 50 || asValue === 65;

            return (
              <React.Fragment key={`tick-group-${angle}`}>
                <Line
                  strokeWidth={1}
                  stroke={color}
                  strokeOpacity={0.7}
                  {...gauge.getTickProps({ angle, length: showText ? 12 : 6 })}
                />
                {showText && (
                  <Text
                    {...gauge.getLabelProps({ angle, offset: -70 })}
                    fill={color}
                  >
                    {asValue}
                  </Text>
                )}
              </React.Fragment>
            );
          })}
        </G>
        <G id="needle" style={{ transform: 'scale(0.7, 0.7)' }}>

          {prevNeedle && (
            <G id="prev-needle">
              <Circle fill={prevColor} fillOpacity={0.3} {...prevNeedle.base} />
              <Circle fill={prevColor} fillOpacity={0.3} {...prevNeedle.tip} />
              <Polyline fill={prevColor} fillOpacity={0.3} points={prevNeedle.points} />
            </G>
          )}

          <Circle fill={color} {...needle.base} />
          <Circle fill={color} {...needle.tip} />
          <Polyline fill={color} points={needle.points} />
        </G>
      </Svg>
    </View>
  );
}
